
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { functions, Timestamp } from '@/firebase';
import { DbTimestamp, TeamMember } from '@run-diffusion/shared';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { ROUTER } from '@/router/constants';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import DoubleConfirmDangerDialog from '@/components/base/DoubleConfirmDangerDialog.vue';
import { getShowTextCounterAt, getTextMaxLengthRule } from '@/utils';
import _trim from 'lodash/trim';
import TeamMembersPicker from '@/components/TeamMembersPicker.vue';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import { TeamUserMixin } from '@/mixins/TeamUserMixin';

interface ApiAccessTokenRow {
	secretId: string
	userId: string
	email: string | null
	companyId: string | null
	label: string | null
	last4: string | null
	createdAt: DbTimestamp | null
	rotatedAt: DbTimestamp | null
}

/** A Timestamp crosses the callable boundary as a plain object. */
interface WireTimestamp {
	_seconds?: number
	_nanoseconds?: number
}

type ApiAccessTokenWireRow = Omit<ApiAccessTokenRow, 'createdAt' | 'rotatedAt'> & {
	createdAt: WireTimestamp | null
	rotatedAt: WireTimestamp | null
}

/**
 * Rebuild the Timestamp the callable flattened, so a row carries what a snapshot
 * would and the template can pipe `.toMillis()` through moment like everywhere
 * else.
 */
const toRow = (wire: ApiAccessTokenWireRow): ApiAccessTokenRow => {
	const rebuild = (value: WireTimestamp | null): DbTimestamp | null => (
		value?._seconds
			? new Timestamp(value._seconds, value._nanoseconds || 0) as DbTimestamp
			: null
	);
	return {
		...wire,
		createdAt: rebuild(wire.createdAt),
		rotatedAt: rebuild(wire.rotatedAt),
	};
};

export default Vue.extend({
	name: 'ApiIntegrationCard',
	mixins: [
		TeamUserMixin,
	],
	components: {
		ConfirmDialog,
		DoubleConfirmDangerDialog,
		TeamMembersPicker,
		DialogOrBottomSheet,
		DialogContent,
		ActionsIsland,
	},
	data () {
		// Matches MAX_LABEL_LENGTH in the managePublicApiAccessToken callable,
		// which truncates past it — the field should stop you rather than silently
		// shortening what you typed.
		const LABEL_MAX_LENGTH: number = 60;
		return {
			LABEL_MAX_LENGTH,
			showCounterAt: getShowTextCounterAt(LABEL_MAX_LENGTH),
			rules: {
				label: [
					getTextMaxLengthRule(LABEL_MAX_LENGTH),
					v => !!_trim(v) || 'Required',
				],
			},
			tokens: [] as ApiAccessTokenRow[],
			loadingTokens: false,
			busySecretId: null as string | null,
			hasMore: false,
			nextCursor: null as string | null,
			loadingMore: false,
			newLabel: '',
			createDialogOpen: false,
			createFormValid: true,
			renameFormValid: true,
			assignedMember: null as TeamMember | null,
			renameDialogOpen: false,
			renameLabel: '',
			renameTarget: null as ApiAccessTokenRow | null,
		};
	},
	props: {
		loadingToken: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		...mapState([
			'team',
			'user',
			'runnitState',
		]),
		...mapGetters([
			'displayLabels',
		]),
		/**
		 * Which tokens this card lists, decided by the account you are acting as.
		 * Acting as a team lists every token scoped to the company; personal lists
		 * your own. They are different credentials reaching different data, so the
		 * card says which set is on screen instead of leaving it to be inferred.
		 */
		isTeamSelected (): boolean {
			// `team` stays BOUND after switching to personal — unbinding it is not
			// how account selection works — so its presence says nothing about
			// which account is selected. The authority is
			// `runnitState.runnitsOwnerSelection`, the same value the account
			// switcher highlights and TeamUserMixin's permission prechecks read.
			return (
				this._get(this.runnitState, 'runnitsOwnerSelection') === RUNNITS_OWNER_SELECTION.TEAM
				&& !!this._get(this.team, 'id')
			);
		},
		companyId (): string | null {
			return this._get(this.team, 'company.id') || null;
		},
		/**
		 * Whether ANY token operation is in flight, which is what every button on
		 * the card is disabled by.
		 *
		 * Disabling and showing progress are deliberately two different questions.
		 * One operation runs at a time, so everything else is disabled while it
		 * does; but the spinner belongs only to the control that started it, which
		 * is `loadingToken` for a create and `busySecretId === token.secretId` for
		 * a row. Binding a spinner to this computed would put one on every button
		 * at once.
		 */
		busy (): boolean {
			return this.loadingToken || !!this.busySecretId;
		},
		/** The rename dialog's own progress, so it spins for its token and no other. */
		renameBusy (): boolean {
			return !!this.renameTarget && this.busySecretId === this.renameTarget.secretId;
		},
		scopeBannerText (): string {
			if (this.isTeamSelected) {
				const teamName: string = this._get(this.team, 'name') || 'team';
				return `You are managing Company API tokens because you have the ${teamName} account selected. Every token here belongs to a member and acts as them, so anything it does is attributed to that person. Deleting one breaks whatever is running on it.`;
			}
			return 'You are managing your Personal API tokens because you have your personal account selected. These act as you, on your personal account only.';
		},
		tokenLabel (): string {
			return this.isTeamSelected ? 'Company API Access Tokens' : 'Personal API Access Tokens';
		},
		/** Matches whatever the company calls its teams, as the roles page does. */
		teamRolesLabel (): string {
			return `${this._get(this.displayLabels, 'team') || 'Team'} Roles`;
		},
		myEmail (): string {
			return this._get(this.user, 'email') || 'you';
		},
		/**
		 * Assigning needs a company (that is what the permission is scoped to) and
		 * MANAGE_API_TOKENS. Without it the dialog is just a name field, and the
		 * token is the caller's own.
		 */
		canAssignToMember (): boolean {
			return this.isTeamSelected && !!this.companyId && this.hasManageApiTokensPermission;
		},
		emptyStateText (): string {
			return this.isTeamSelected
				? 'Nobody has created a company API token yet.'
				: 'You have no personal API tokens yet.';
		},
		regenerateWarning (): string {
			return 'The current value stops working immediately. Anything running on it has to be updated with the new one. The token keeps its name and scope.';
		},
		tokenDescription (): string {
			return this.isTeamSelected
				? 'Bearer tokens for team API use, scoped to this company.'
				: 'Bearer tokens that authenticate as you: browse your library, manage your boards, and upload assets.';
		},
	},
	watch: {
		// Switching accounts changes which set of tokens is being managed, so the
		// list has to follow. Without this the company list would linger on screen
		// after a switch to personal.
		isTeamSelected: {
			immediate: true,
			handler () {
				this.fetchTokens();
			},
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		_trim,
		goToTeamRoles () {
			this.routerPush(this.$route, this.$router, { name: ROUTER.TEAM_ROLES });
		},
		openReportingDocumentationLink () {
			// The v2 docs on the marketing site. The API server serves only a
			// machine-readable schema, no browsable UI.
			window.open('https://www.rundiffusion.com/docs/api/overview', '_blank');
		},
		/** Only the owner can regenerate; an admin can delete but never mint a secret as someone else. */
		isMine (token: ApiAccessTokenRow): boolean {
			return token.userId === this._get(this.user, 'id');
		},
		deleteWarning (token: ApiAccessTokenRow): string {
			const name: string = token.label || 'This token';
			if (!this.isMine(token)) {
				return `${name} belongs to ${token.email || 'another member'}. Deleting it stops every integration running on it, immediately and permanently.`;
			}
			return `${name} stops working immediately and permanently. Anything running on it will start failing.`;
		},
		onCallError (e: any, fallback: string) {
			console.error(e);
			this.updateSnackbar({
				status: SNACKBAR_STATUS.ERROR,
				message: e?.message || fallback,
				show: true,
			});
		},
		/** First page. Resets the cursor, so a refresh or account switch starts over. */
		/**
		 * Reload the first page.
		 *
		 * `quiet` keeps the rows on screen and skips the skeleton. It is for the
		 * reload that FOLLOWS a rename, regenerate, or delete: those act on one
		 * token, and blanking the whole card to redraw a single row is a card-wide
		 * loading state for a per-row operation. The row is already showing its own
		 * progress, so there is nothing left for the skeleton to communicate.
		 *
		 * A loud reload still blanks first, because switching accounts or hitting
		 * Refresh really is replacing the whole list.
		 */
		async fetchTokens (quiet: boolean = false) {
			// A company view needs a company; without one there is nothing to list,
			// and calling would just 400.
			if (this.isTeamSelected && !this.companyId) {
				this.tokens = [];
				this.hasMore = false;
				this.nextCursor = null;
				return;
			}
			if (!quiet) {
				// Dropped before the call, not after it: these rows belong to whichever
				// account was selected a moment ago.
				this.tokens = [];
				this.hasMore = false;
				this.nextCursor = null;
				this.loadingTokens = true;
			}
			try {
				await this.loadPage(null, quiet);
			} finally {
				if (!quiet) this.loadingTokens = false;
			}
		},
		/** Next page, appended. The button is hidden unless the server said there is one. */
		async loadMoreTokens () {
			if (!this.hasMore || !this.nextCursor || this.loadingMore) return;
			this.loadingMore = true;
			try {
				await this.loadPage(this.nextCursor);
			} finally {
				this.loadingMore = false;
			}
		},
		async loadPage (startAfterSecretId: string | null, replace: boolean = false) {
			try {
				// One callable; `companyId` picks the list, the same way create does.
				const fn = functions.httpsCallable('listPublicApiAccessTokens');
				const result = await fn({
					...(this.isTeamSelected ? { companyId: this.companyId } : {}),
					...(startAfterSecretId ? { startAfterSecretId } : {}),
				});
				const data = result?.data as {
					success?: boolean;
					tokens?: ApiAccessTokenWireRow[];
					message?: string;
					hasMore?: boolean;
					nextCursor?: string | null;
				};
				if (data?.success) {
					// Append on a page fetch. A loud first fetch already emptied the
					// list, so appending replaces it; a quiet one deliberately did not,
					// and swaps the rows in here instead so they never blink out.
					const rows = (data.tokens || []).map(toRow);
					this.tokens = replace ? rows : [...this.tokens, ...rows];
					this.hasMore = !!data.hasMore;
					this.nextCursor = data.nextCursor || null;
				} else {
					this.hasMore = false;
					this.nextCursor = null;
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: data?.message || 'Could not load API tokens',
						show: true,
					});
				}
			} catch (e: any) {
				this.hasMore = false;
				this.nextCursor = null;
				this.onCallError(e, 'Could not load API tokens');
			}
		},
		openCreateDialog () {
			this.newLabel = '';
			this.assignedMember = null;
			this.createDialogOpen = true;
			this.resetCreateForm();
		},
		/**
		 * Clearing the model is not enough: the v-form keeps its validation state,
		 * so a dialog closed on a "Required" error reopens still showing it.
		 * Deferred a tick because the form may not be mounted yet on first open.
		 */
		resetCreateForm () {
			this.$nextTick(() => {
				const form: any = this.$refs.createForm;
				if (form) form.resetValidation();
			});
		},
		onSelectAssignee (member: TeamMember | null) {
			// A member who has not accepted their invite has no `userId` to attribute
			// the token to, so treat that as no selection.
			this.assignedMember = member?.userId ? member : null;
		},
		onRenameToken (token: ApiAccessTokenRow) {
			this.renameTarget = token;
			this.renameLabel = token.label || '';
			this.renameDialogOpen = true;
			this.$nextTick(() => {
				const form: any = this.$refs.renameForm;
				if (form) form.resetValidation();
			});
		},
		async onConfirmRename (close: () => void) {
			const token: ApiAccessTokenRow | null = this.renameTarget;
			if (!token) return;
			this.busySecretId = token.secretId;
			try {
				const fn = functions.httpsCallable('managePublicApiAccessToken');
				const result = await fn({ action: 'edit', secretId: token.secretId, label: this.renameLabel });
				const data = result?.data as { success?: boolean; message?: string };
				if (data?.success) {
					await this.fetchTokens(true);
					if (close) close();
					this.renameTarget = null;
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: data?.message || 'Could not rename that API token',
						show: true,
					});
				}
			} catch (e: any) {
				this.onCallError(e, 'Could not rename that API token');
			} finally {
				this.busySecretId = null;
			}
		},
		onConfirmCreate () {
			this.onCreateToken();
		},
		onCreateToken () {
			// The page owns the reveal dialog, so it owns the create call. It calls
			// back through `refresh` because only this component knows which list is
			// on screen.
			this.$emit('create-token', {
				companyId: this.isTeamSelected ? this.companyId : null,
				label: (this.newLabel || '').trim() || null,
				userId: this.canAssignToMember ? (this.assignedMember?.userId || null) : null,
				onDone: (created: boolean) => {
					if (created) {
						this.newLabel = '';
						this.assignedMember = null;
						this.createDialogOpen = false;
						this.resetCreateForm();
					}
					this.fetchTokens(true);
				},
			});
		},
		/**
		 * Unlike rename and delete, the callable for a roll lives on the parent
		 * (it owns the reveal dialog), so this reaches it through `$emit`.
		 *
		 * `$emit` is SYNCHRONOUS — it returns the component, not a promise — so
		 * there is nothing here to await, and clearing `busySecretId` in a
		 * `finally` cleared it in the same tick, before the request had even been
		 * sent. That left the row with no loading state at all and made the
		 * parent's shared create flag the only visible sign of progress, which is
		 * why regenerating spun the Create token button.
		 *
		 * The parent's `onDone` is the completion signal, so the row stays busy
		 * until it fires.
		 */
		onRollToken (token: ApiAccessTokenRow, close: () => void) {
			this.busySecretId = token.secretId;
			this.$emit('roll-token', {
				secretId: token.secretId,
				onDone: async (ok: boolean) => {
					try {
						// A failed roll left the old secret working and the list
						// unchanged, so there is nothing to refetch; the parent has
						// already raised the error.
						if (ok) await this.fetchTokens(true);
					} finally {
						this.busySecretId = null;
						if (close) close();
					}
				},
			});
		},
		async onDeleteToken (token: ApiAccessTokenRow, close: () => void) {
			this.busySecretId = token.secretId;
			try {
				const fn = functions.httpsCallable('managePublicApiAccessToken');
				const result = await fn({ action: 'delete', secretId: token.secretId });
				const data = result?.data as { success?: boolean; message?: string };
				if (data?.success) {
					await this.fetchTokens(true);
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: data?.message || 'Could not delete that API token',
						show: true,
					});
				}
			} catch (e: any) {
				this.onCallError(e, 'Could not delete that API token');
			} finally {
				this.busySecretId = null;
				if (close) close();
			}
		},
	},
});
